import { Controller } from "@hotwired/stimulus"

// § 3.3 Tier 3 — Generic SFTP "Test connection" round-trip.
//
// Wires the Test connection button on the connection-detail Defaults
// panel to POST /connections/:id/sftp_test → opens an SFTP session,
// lists the configured remote path, returns the directory entries
// (or the typed SftpError shape on failure with the actual server-
// reported fingerprint on host-key mismatch). Surfaces the result
// inline next to the Save button so admins see the listing or the
// error without leaving the page.
export default class extends Controller {
  static targets = ["form", "result"]
  static values = { testUrl: String }

  async test(event) {
    event.preventDefault()
    this._setBusy()

    try {
      const response = await fetch(this.testUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this._csrfToken(),
          "Accept": "application/json"
        }
      })
      const data = await response.json()
      if (response.ok && data.ok) {
        this._renderSuccess(data)
      } else {
        this._renderFailure(data, response.status)
      }
    } catch (e) {
      this._renderFailure({ error: e.message || "Network error" }, 0)
    }
  }

  _setBusy() {
    this.resultTarget.classList.remove("hidden", "text-rose-700", "text-emerald-700")
    this.resultTarget.classList.add("text-gray-500")
    this.resultTarget.textContent = "Testing connection…"
  }

  _renderSuccess(data) {
    const entries = (data.entries || []).slice(0, 6).join(", ")
    const more = data.total_entries > 6 ? ` (+${data.total_entries - 6} more)` : ""
    this.resultTarget.classList.remove("hidden", "text-gray-500", "text-rose-700")
    this.resultTarget.classList.add("text-emerald-700")
    this.resultTarget.textContent =
      `Connected to ${data.remote_path} in ${data.response_time_ms}ms — ${data.total_entries} entries: ${entries}${more}`
  }

  _renderFailure(data, status) {
    this.resultTarget.classList.remove("hidden", "text-gray-500", "text-emerald-700")
    this.resultTarget.classList.add("text-rose-700")

    let msg = data.error || `Test failed (status ${status})`
    if (data.kind === "host_key_mismatch" && data.remote_fingerprint) {
      msg += `. Server reports ${data.remote_fingerprint} — copy this into the host-key fingerprint field if it's the value you expect.`
    }
    this.resultTarget.textContent = msg
  }

  _csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }
}
